import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import Layout from '../../components/Layout'
import Spinner from '../../components/Spinner'
import { getSettings, updateSettings } from '../../api/settings'
import { useAuth } from '../../context/AuthContext'
import { useT } from '../../i18n'

export default function SettingsPage() {
  const { user, isSuperadmin } = useAuth()
  const navigate = useNavigate()
  const lang = user?.language ?? 'ru'
  const tr = useT(lang)
  const qc = useQueryClient()

  const { data: settings, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: getSettings,
  })

  const [form, setForm] = useState({
    rate_3_months: '', rate_6_months: '', rate_9_months: '', rate_12_months: '',
    min_down_payment_pct: '', max_months: '', usd_rate: '', card_number: '', card_holder: '',
    limit_bronze: '', limit_silver: '', limit_gold: '', support_username: '',
    surcharge_lt15_3m: '', surcharge_lt15_6m: '', surcharge_lt15_9m: '', surcharge_lt15_12m: '',
    surcharge_lt10_3m: '', surcharge_lt10_6m: '', surcharge_lt10_9m: '', surcharge_lt10_12m: '',
    surcharge_lt5_3m: '', surcharge_lt5_6m: '', surcharge_lt5_9m: '', surcharge_lt5_12m: '',
  })

  useEffect(() => {
    if (settings) {
      setForm({
        rate_3_months: String(settings.rate_3_months),
        rate_6_months: String(settings.rate_6_months),
        rate_9_months: String(settings.rate_9_months),
        rate_12_months: String(settings.rate_12_months),
        min_down_payment_pct: String(settings.min_down_payment_pct),
        max_months: String(settings.max_months),
        usd_rate: String(settings.usd_rate),
        card_number: settings.card_number,
        card_holder: settings.card_holder,
        limit_bronze: String(settings.limit_bronze ?? 500),
        limit_silver: String(settings.limit_silver ?? 1500),
        limit_gold: String(settings.limit_gold ?? 5000),
        support_username: settings.support_username ?? 'ansor22_support',
        surcharge_lt15_3m: String(settings.surcharge_lt15_3m ?? 0),
        surcharge_lt15_6m: String(settings.surcharge_lt15_6m ?? 0),
        surcharge_lt15_9m: String(settings.surcharge_lt15_9m ?? 0),
        surcharge_lt15_12m: String(settings.surcharge_lt15_12m ?? 0),
        surcharge_lt10_3m: String(settings.surcharge_lt10_3m ?? 0),
        surcharge_lt10_6m: String(settings.surcharge_lt10_6m ?? 0),
        surcharge_lt10_9m: String(settings.surcharge_lt10_9m ?? 0),
        surcharge_lt10_12m: String(settings.surcharge_lt10_12m ?? 0),
        surcharge_lt5_3m: String(settings.surcharge_lt5_3m ?? 0),
        surcharge_lt5_6m: String(settings.surcharge_lt5_6m ?? 0),
        surcharge_lt5_9m: String(settings.surcharge_lt5_9m ?? 0),
        surcharge_lt5_12m: String(settings.surcharge_lt5_12m ?? 0),
      })
    }
  }, [settings])

  const saveMut = useMutation({
    mutationFn: () => updateSettings({
      rate_3_months: Number(form.rate_3_months),
      rate_6_months: Number(form.rate_6_months),
      rate_9_months: Number(form.rate_9_months),
      rate_12_months: Number(form.rate_12_months),
      min_down_payment_pct: Number(form.min_down_payment_pct),
      max_months: Number(form.max_months),
      usd_rate: Number(form.usd_rate),
      card_number: form.card_number,
      card_holder: form.card_holder,
      limit_bronze: Number(form.limit_bronze),
      limit_silver: Number(form.limit_silver),
      limit_gold: Number(form.limit_gold),
      support_username: form.support_username,
      surcharge_lt15_3m: Number(form.surcharge_lt15_3m),
      surcharge_lt15_6m: Number(form.surcharge_lt15_6m),
      surcharge_lt15_9m: Number(form.surcharge_lt15_9m),
      surcharge_lt15_12m: Number(form.surcharge_lt15_12m),
      surcharge_lt10_3m: Number(form.surcharge_lt10_3m),
      surcharge_lt10_6m: Number(form.surcharge_lt10_6m),
      surcharge_lt10_9m: Number(form.surcharge_lt10_9m),
      surcharge_lt10_12m: Number(form.surcharge_lt10_12m),
      surcharge_lt5_3m: Number(form.surcharge_lt5_3m),
      surcharge_lt5_6m: Number(form.surcharge_lt5_6m),
      surcharge_lt5_9m: Number(form.surcharge_lt5_9m),
      surcharge_lt5_12m: Number(form.surcharge_lt5_12m),
    }),
    onSuccess: () => { toast.success(tr('settingsSaved')); qc.invalidateQueries({ queryKey: ['settings'] }) },
    onError: (e: any) => toast.error(e?.response?.data?.detail ?? tr('error')),
  })

  if (isLoading) return <Spinner className="h-screen" />

  const f = (key: keyof typeof form) => ({
    value: form[key],
    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((prev) => ({ ...prev, [key]: e.target.value })),
    disabled: !isSuperadmin,
  })

  return (
    <Layout title={tr('settings')} hideNav>
      <button onClick={() => navigate(-1)} style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#A8A8AE', marginBottom: 16, fontSize: 13, background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
        Назад
      </button>
      <div className="flex flex-col gap-4">
        {/* Interest rates */}
        <div className="card">
          <p className="section-title mb-3">{tr('interestRates')}</p>
          <div className="grid grid-cols-2 gap-3">
            <Field label={tr('rateUpTo3')} {...f('rate_3_months')} type="number" />
            <Field label={tr('rateUpTo6')} {...f('rate_6_months')} type="number" />
            <Field label={tr('rateUpTo9')} {...f('rate_9_months')} type="number" />
            <Field label={tr('rateFrom10')} {...f('rate_12_months')} type="number" />
          </div>
        </div>

        {/* Down-payment surcharges */}
        <div className="card">
          <p className="section-title mb-1">
            {lang === 'ru' ? 'Надбавки за низкий первоначальный взнос' : 'Past to\'lov uchun qo\'shimcha foiz'}
          </p>
          <p style={{ fontSize: 11, color: '#5E6070', marginBottom: 12 }}>
            {lang === 'ru'
              ? 'Прибавляется к базовой ставке. Порог считается от цены товара.'
              : 'Asosiy foizga qo\'shiladi. Chegara tovar narxidan hisoblanadi.'}
          </p>
          {([
            { key: 'lt15', label: lang === 'ru' ? 'Взнос < 15%' : 'To\'lov < 15%' },
            { key: 'lt10', label: lang === 'ru' ? 'Взнос < 10%' : 'To\'lov < 10%' },
            { key: 'lt5',  label: lang === 'ru' ? 'Взнос < 5%'  : 'To\'lov < 5%' },
          ] as const).map(({ key, label }) => (
            <div key={key} style={{ marginBottom: 16 }}>
              <p style={{ fontSize: 11, fontWeight: 600, color: '#EAB308', marginBottom: 8 }}>{label}</p>
              <div className="grid grid-cols-2 gap-3">
                <Field label={tr('rateUpTo3')}  {...f(`surcharge_${key}_3m`  as keyof typeof form)} type="number" />
                <Field label={tr('rateUpTo6')}  {...f(`surcharge_${key}_6m`  as keyof typeof form)} type="number" />
                <Field label={tr('rateUpTo9')}  {...f(`surcharge_${key}_9m`  as keyof typeof form)} type="number" />
                <Field label={tr('rateFrom10')} {...f(`surcharge_${key}_12m` as keyof typeof form)} type="number" />
              </div>
            </div>
          ))}
        </div>

        {/* Other settings */}
        <div className="card">
          <p className="section-title mb-3">{tr('general')}</p>
          <div className="flex flex-col gap-3">
            <Field label={tr('minDownPaymentLabel')} {...f('min_down_payment_pct')} type="number" />
            <Field label={tr('maxTermLabel')} {...f('max_months')} type="number" />
          </div>
        </div>

        {/* Exchange rate */}
        <div className="card">
          <p className="section-title mb-3">{tr('exchangeRate')}</p>
          <Field label={lang === 'ru' ? '1 $ = ? сум' : '1 $ = ? so\'m'} {...f('usd_rate')} type="number" />
        </div>

        {/* Card details */}
        <div className="card">
          <p className="section-title mb-3">{tr('cardDetails')}</p>
          <div className="flex flex-col gap-3">
            <Field label={tr('cardNumber')} {...f('card_number')} />
            <Field label={tr('cardHolder')} {...f('card_holder')} />
          </div>
        </div>

        {/* Tier limits */}
        <div className="card">
          <p className="section-title mb-3">{tr('tierLimits')}</p>
          <div className="grid grid-cols-3 gap-3">
            <Field label={tr('limitBronze')} {...f('limit_bronze')} type="number" />
            <Field label={tr('limitSilver')} {...f('limit_silver')} type="number" />
            <Field label={tr('limitGold')}   {...f('limit_gold')}   type="number" />
          </div>
          <p style={{ marginTop: 8, fontSize: 11, color: '#5E6070' }}>
            {lang === 'ru'
              ? 'Лимит назначается клиенту автоматически при изменении уровня'
              : 'Limit mijozga daraja o\'zgarganda avtomatik beriladi'}
          </p>
        </div>

        {/* Support */}
        <div className="card">
          <p className="section-title mb-3">{tr('support')}</p>
          <Field label={tr('supportUsername')} {...f('support_username')} />
        </div>

        {isSuperadmin && (
          <button
            onClick={() => saveMut.mutate()}
            disabled={saveMut.isPending}
            className="btn-primary disabled:opacity-40"
          >
            {tr('save')}
          </button>
        )}
        {!isSuperadmin && (
          <p className="text-center text-xs text-tg-hint">{tr('superadminOnly')}</p>
        )}

      </div>
    </Layout>
  )
}

function Field({ label, value, onChange, type = 'text', disabled }: {
  label: string; value: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  type?: string; disabled?: boolean
}) {
  return (
    <div>
      <p className="section-title">{label}</p>
      <input
        type={type}
        className="input mt-1 disabled:opacity-60"
        value={value}
        onChange={onChange}
        disabled={disabled}
      />
    </div>
  )
}
